'use client';

import { cn } from '@lib/util/cn';
import { useAuth } from '@modules/common/components/auth-provider';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { User } from '@modules/common/icons';
import { useUnread } from '../unread-provider';

type AccountBadgeProps = {
  className?: string;
  noLink?: boolean;
};

export default function AccountBadge({ className, noLink }: AccountBadgeProps) {
  const { totalUnread } = useUnread();
  const { isLoggedIn } = useAuth();

  const inner = (
    <>
      <User size="20" aria-hidden="true" />
      {isLoggedIn && <UnreadIndicator count={totalUnread} />}
    </>
  );

  if (noLink) {
    return (
      <span className={cn(className, 'relative text-white p-3 inline-flex')}>
        {inner}
      </span>
    );
  }

  return (
    <LocalizedClientLink
      href="/account"
      prefetch={false}
      className={cn(className, 'relative text-white p-3')}
      aria-label={buildAriaLabel(isLoggedIn, totalUnread)}
      data-testid="nav-account-link-mobile"
    >
      {inner}
    </LocalizedClientLink>
  );
}

function UnreadIndicator({ count }: { count: number }) {
  if (count > 0) {
    return (
      <span
        className="absolute top-2 right-[3px] flex items-center justify-center w-[18px] h-[18px] text-[10px] font-regular leading-none text-fg-neutral-strong bg-surface-interactive-default rounded-full"
        aria-live="polite"
        data-testid="unread-account-badge"
      >
        {count > 99 ? '99+' : count}
      </span>
    );
  }

  return (
    <span
      className="absolute top-3 right-2.5 min-w-1 h-1 rounded-full bg-surface-interactive-soft"
      aria-hidden="true"
      data-testid="is-logged-in-badge"
    />
  );
}

const buildAriaLabel = (
  isLoggedIn: boolean | undefined,
  count: number,
): string => {
  if (count === 1) {
    return 'Account, 1 unread message from your pharmacist';
  }

  if (count > 1) {
    return `Account, ${count} unread messages from your pharmacist`;
  }

  if (isLoggedIn) {
    return 'Account (logged in)';
  }

  return 'Account';
};
